import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { MATERIALS } from "./materials";
import { Holo } from "./holo";

export const BOARD = 10;
const CLEAR = 0.04;
export const VIEWS = [
  { name: "Isometric", pos: [0, 11.5, 13.5] },
  { name: "Top-down", pos: [0, 17.5, 0.01] },
  { name: "Low angle", pos: [11, 3.4, 7] },
  { name: "Side profile", pos: [0, 2.8, 14.5] },
  { name: "Macro", pos: [3.4, 3.6, 4.4] },
];

const download = (url, name) => { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); };

export class Engine {
  constructor(container, { onTelemetry, onHistory, onParamUI, onAuto, settings, instrument }) {
    this.onAuto = onAuto;
    this.style = instrument?.style || "pins"; this.look = instrument?.look || {}; this.q = { t: 0, n: 0, level: 0 };
    this.container = container; this.onTelemetry = onTelemetry; this.onHistory = onHistory; this.onParamUI = onParamUI;
    this.s = { ...settings };
    this.undoStack = []; this.redoStack = [];
    this.simT = 0; this.view = 0; this.frames = 0; this.telT = performance.now(); this.energyAcc = 0;
    this.ctx = { overlay: null, g: this.s, params: {}, pushHistory: () => this.pushHistory(), simTime: () => this.simT, setUI: (k, v) => { this.ctx.params[k] = v; this.onParamUI?.(k, v); } };
    this.initRenderer(); this.ctx.lowPower = this.software; this.initScene(); this.buildPins(); this.applySettings(Object.keys(this.s));
    this.bindPointer();
    this.ro = new ResizeObserver(() => this.resize()); this.ro.observe(container); this.resize();
    this.last = performance.now(); this.loop = this.loop.bind(this); this.raf = requestAnimationFrame(this.loop);
  }

  initRenderer() {
    const r = (this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" }));
    this.coarse = window.matchMedia?.("(pointer: coarse)").matches;
    r.setPixelRatio(Math.min(window.devicePixelRatio, this.coarse ? 1.5 : 2));
    r.shadowMap.enabled = true; r.shadowMap.type = THREE.PCFSoftShadowMap;
    r.toneMapping = THREE.ACESFilmicToneMapping; r.toneMappingExposure = 1.2;
    const gl = r.getContext(), ext = gl.getExtension("WEBGL_debug_renderer_info");
    const q = new URLSearchParams(window.location.search).get("quality");
    this.software = q === "low" || (q !== "high" && /swiftshader|llvmpipe|software|basic render/i.test(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : ""));
    if (this.software) { r.setPixelRatio(0.5); r.shadowMap.enabled = false; this.s.density = Math.min(this.s.density, 40); }
    r.domElement.style.touchAction = "none"; r.domElement.setAttribute("data-testid", "pin-board-canvas");
    this.container.appendChild(r.domElement);
  }

  initScene() {
    const sc = (this.scene = new THREE.Scene());
    sc.background = new THREE.Color("#03050a"); sc.fog = new THREE.Fog("#03050a", 30, 90);
    const pm = new THREE.PMREMGenerator(this.renderer);
    sc.environment = pm.fromScene(this.labEnvironment(), 0.03).texture;
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 140);
    this.camera.position.set(...VIEWS[0].pos);
    const c = (this.controls = new OrbitControls(this.camera, this.renderer.domElement));
    c.enableDamping = true; c.dampingFactor = 0.08; c.maxPolarAngle = Math.PI * 0.48; c.minDistance = 2.5; c.maxDistance = 48;
    c.target.set(0, 0.5, 0); c.addEventListener("start", () => (this.tween = null));

    this.hemi = new THREE.HemisphereLight(0xdbe8ff, 0x1a2230, 0.9);
    this.fill = new THREE.DirectionalLight(0xe8f0ff, 0.8); this.fill.position.set(0, 9, 13);
    const key = (this.key = new THREE.DirectionalLight(0xffffff, 2.0));
    key.position.set(-6, 12, 5); key.castShadow = true; key.shadow.mapSize.setScalar(this.coarse ? 1024 : 2048);
    Object.assign(key.shadow.camera, { left: -8, right: 8, top: 8, bottom: -8, near: 1, far: 40 });
    key.shadow.bias = -0.0004; key.shadow.normalBias = 0.02;
    this.rim = new THREE.DirectionalLight(0x00f0ff, 0.9); this.rim.position.set(8, 4, -7);
    this.warm = new THREE.PointLight(0xffb800, 7, 30, 1.6); this.warm.position.set(7, 5, 7);
    sc.add(this.hemi, this.fill, key, this.rim, this.warm);

    this.plate = new THREE.Mesh(new THREE.BoxGeometry(BOARD + 0.6, 1, BOARD + 0.6), new THREE.MeshStandardMaterial({ color: 0x0c111b, metalness: 0.7, roughness: 0.45 }));
    this.plate.receiveShadow = true;
    this.edges = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(BOARD + 0.62, 1, BOARD + 0.62)), new THREE.LineBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.55 }));
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(70, 70), new THREE.ShadowMaterial({ opacity: 0.45 }));
    floor.rotation.x = -Math.PI / 2; floor.position.y = -2.41; floor.receiveShadow = true;
    this.overlay = new THREE.Group(); this.ctx.overlay = this.overlay;
    this.ring = new THREE.Mesh(new THREE.RingGeometry(0.94, 1, 72), new THREE.MeshBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthTest: false }));
    this.ring.rotation.x = -Math.PI / 2; this.ring.renderOrder = 10; this.ring.visible = false;
    sc.add(this.plate, this.edges, floor, this.overlay, this.ring);
    this.holo = new Holo(sc, this.software);
    this.pinMat = this.software ? new THREE.MeshStandardMaterial() : new THREE.MeshPhysicalMaterial();
  }

  labEnvironment() {
    const env = new THREE.Scene(), box = new THREE.BoxGeometry(1, 1, 1);
    const room = new THREE.Mesh(box, new THREE.MeshBasicMaterial({ color: 0x3a4558, side: THREE.BackSide }));
    room.scale.set(30, 14, 30); room.position.y = 5; env.add(room);
    const strip = (color, intensity, pos, scale) => {
      const m = new THREE.Mesh(box, new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(intensity) }));
      m.position.set(...pos); m.scale.set(...scale); env.add(m);
    };
    strip(0xffffff, 2.5, [0, 11.8, 0], [16, 0.2, 2.2]);
    strip(0xffffff, 1.8, [-6, 11.8, 4], [2, 0.2, 10]);
    strip(0x00f0ff, 3, [14.8, 4, -4], [0.2, 2, 14]);
    strip(0xffb800, 3, [-14.8, 3, 6], [0.2, 1.4, 10]);
    strip(0xffffff, 2, [0, 5, -14.8], [20, 1, 0.2]);
    return env;
  }

  buildSurface(N, sp) {
    if (this.surf) { this.scene.remove(this.surf); this.surf.geometry.dispose(); this.surf.material.dispose(); }
    const L = this.look, geo = new THREE.PlaneGeometry(BOARD - sp, BOARD - sp, N - 1, N - 1); geo.rotateX(-Math.PI / 2);
    geo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(N * N * 3).fill(1), 3));
    let obj;
    if (this.style === "points") obj = new THREE.Points(geo, new THREE.PointsMaterial({ size: sp * 0.85, vertexColors: true, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false }));
    else {
      const Mat = this.software ? THREE.MeshStandardMaterial : THREE.MeshPhysicalMaterial, wire = this.style === "wire";
      const opts = { color: L.color || "#ffffff", metalness: L.metalness ?? 0.2, roughness: L.roughness ?? 0.4, vertexColors: true, side: THREE.DoubleSide, wireframe: wire, transparent: wire, opacity: wire ? 0.8 : 1 };
      if (!this.software) Object.assign(opts, { clearcoat: L.clearcoat ?? 0, clearcoatRoughness: 0.15 });
      obj = new THREE.Mesh(geo, new Mat(opts)); obj.castShadow = obj.receiveShadow = !wire;
    }
    obj.visible = this.style !== "none"; obj.frustumCulled = false;
    this.surf = obj; this.scene.add(obj);
  }

  buildPins() {
    if (this.pins) { this.scene.remove(this.pins); this.pins.geometry.dispose(); this.pins.dispose(); this.pins = null; }
    const N = this.s.density, count = N * N, sp = BOARD / N, r = sp * 0.4, travel = this.s.pinHeight;
    if (this.style !== "pins") {
      this.buildSurface(N, sp); this.plate.visible = this.edges.visible = false;
      const px = new Float32Array(N); for (let j = 0; j < N; j++) px[j] = -BOARD / 2 + sp * (j + 0.5);
      const oldH = this.H; this.H = new Float32Array(count).fill(1); this.V = new Float32Array(count); if (oldH && oldH.length === count) this.H.set(oldH);
      Object.assign(this.ctx, { N, count, sp, half: BOARD / 2, px, travel, top: CLEAR + travel, height: this.H, target: new Float32Array(count).fill(1), colors: new Float32Array(count * 3).fill(1), surfaceY: (h) => CLEAR + (h + 1) * 0.5 * travel, sampleY: (x, z) => this.sampleY(x, z) });
      this.colorsDirty = true; return;
    }
    const L = travel + CLEAR + 0.2, prof = [new THREE.Vector2(r * 0.96, -L), new THREE.Vector2(r, -r * 0.6)];
    for (let k = 1; k <= 4; k++) { const a = (k / 4) * Math.PI * 0.5; prof.push(new THREE.Vector2(Math.max(0.0001, r * Math.cos(a)), -r * 0.6 + r * 0.6 * Math.sin(a))); }
    const mesh = new THREE.InstancedMesh(new THREE.LatheGeometry(prof, N > 120 ? 7 : 10), this.pinMat, count);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.castShadow = mesh.receiveShadow = true; mesh.frustumCulled = false;
    const px = new Float32Array(N); for (let j = 0; j < N; j++) px[j] = -BOARD / 2 + sp * (j + 0.5);
    const m = new THREE.Matrix4();
    for (let rr = 0; rr < N; rr++) for (let c = 0; c < N; c++) { m.makeTranslation(px[c], CLEAR + travel, px[rr]); mesh.setMatrixAt(rr * N + c, m); }
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(count * 3).fill(1), 3);
    mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.pins = mesh; this.scene.add(mesh);
    const oldH = this.H;
    this.H = new Float32Array(count).fill(1); this.V = new Float32Array(count);
    if (oldH && oldH.length === count) this.H.set(oldH);
    const ph = travel + 0.6;
    this.plate.scale.y = ph; this.plate.position.y = -ph / 2; this.edges.scale.y = ph; this.edges.position.y = -ph / 2;
    Object.assign(this.ctx, {
      N, count, sp, half: BOARD / 2, px, travel, top: CLEAR + travel, height: this.H,
      target: new Float32Array(count).fill(1), colors: new Float32Array(count * 3).fill(1),
      surfaceY: (h) => CLEAR + (h + 1) * 0.5 * travel, sampleY: (x, z) => this.sampleY(x, z),
    });
    this.colorsDirty = true;
  }

  sampleY(x, z) {
    const { N, sp, half } = this.ctx;
    const c = Math.min(N - 1, Math.max(0, Math.floor((x + half) / sp))), r = Math.min(N - 1, Math.max(0, Math.floor((z + half) / sp)));
    return CLEAR + (this.H[r * N + c] + 1) * 0.5 * this.ctx.travel;
  }

  applySettings(keys) {
    const s = this.s;
    if (keys.includes("material")) {
      const M = MATERIALS[s.material], mt = this.pinMat;
      mt.color.set(M.color); mt.metalness = M.metalness; mt.roughness = M.roughness;
      if (mt.isMeshPhysicalMaterial) { mt.clearcoat = M.clearcoat; mt.clearcoatRoughness = M.clearcoatRoughness; mt.anisotropy = M.anisotropy; }
      mt.needsUpdate = true;
    }
    if (keys.includes("light")) {
      const l = s.light;
      this.key.intensity = 2.0 * l; this.hemi.intensity = 0.9 * l; this.fill.intensity = 0.8 * l; this.rim.intensity = 0.9 * l; this.warm.intensity = 7 * l;
      this.scene.environmentIntensity = 0.25 + 0.5 * l;
    }
    if (keys.includes("interact")) {
      const c = this.controls;
      c.mouseButtons = s.interact ? { LEFT: null, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.ROTATE } : { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
      c.touches = s.interact ? { ONE: null, TWO: THREE.TOUCH.DOLLY_ROTATE } : { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
      if (!s.interact) this.ring.visible = false;
    }
  }

  setSettings(patch) {
    const keys = Object.keys(patch);
    Object.assign(this.s, patch);
    if (keys.includes("density") || keys.includes("pinHeight")) {
      this.buildPins(); this.inst?.resize?.(this.ctx);
      if (keys.includes("density")) { this.undoStack = []; this.redoStack = []; this.emitHistory(); }
    }
    this.applySettings(keys);
  }

  setMode(def, params) {
    this.inst?.dispose?.();
    this.def = def; this.ctx.params = { ...params };
    this.ctx.target.fill(def.rest ?? 1);
    this.inst = def.create(this.ctx);
    this.undoStack = []; this.redoStack = []; this.emitHistory();
  }

  setParam(key, val) { this.ctx.params[key] = val; this.inst?.onParams?.(key); }
  action(id, payload) { return this.inst?.action?.(id, payload); }

  // ---------- history ----------
  emitHistory() { this.onHistory?.({ supported: !!this.inst?.snapshot, canUndo: this.undoStack.length > 0, canRedo: this.redoStack.length > 0 }); }
  pushHistory() {
    if (!this.inst?.snapshot) return;
    this.undoStack.push(this.inst.snapshot()); if (this.undoStack.length > 50) this.undoStack.shift();
    this.redoStack = []; this.emitHistory();
  }
  undo() { if (!this.undoStack.length) return; this.redoStack.push(this.inst.snapshot()); this.inst.restore(this.undoStack.pop()); this.emitHistory(); }
  redo() { if (!this.redoStack.length) return; this.undoStack.push(this.inst.snapshot()); this.inst.restore(this.redoStack.pop()); this.emitHistory(); }
  clear() { this.pushHistory(); this.inst?.clear?.(); }
  resetExperiment(def, params) { this.setMode(def, params); this.simT = 0; this.setView(0); }

  // ---------- camera ----------
  setView(i) {
    this.view = i;
    this.tween = { from: this.camera.position.clone(), to: new THREE.Vector3(...VIEWS[i].pos).multiplyScalar(this.camFactor || 1), fromT: this.controls.target.clone(), toT: new THREE.Vector3(0, 0.5, 0), t: 0 };
    return VIEWS[i].name;
  }
  swapPerspective() { return this.setView((this.view + 1) % VIEWS.length); }
  updateTween(dt) {
    const tw = this.tween; if (!tw) return;
    tw.t = Math.min(1, tw.t + dt / 0.9);
    const e = tw.t < 0.5 ? 4 * tw.t ** 3 : 1 - (-2 * tw.t + 2) ** 3 / 2;
    this.camera.position.lerpVectors(tw.from, tw.to, e); this.controls.target.lerpVectors(tw.fromT, tw.toT, e);
    if (tw.t >= 1) this.tween = null;
  }

  // ---------- pointer ----------
  bindPointer() {
    const el = this.renderer.domElement;
    this.touches = new Set(); this.raycaster = new THREE.Raycaster(); this.ndc = new THREE.Vector2();
    this.onDown = (e) => {
      if (e.pointerType === "touch") this.touches.add(e.pointerId);
      if (!this.s.interact || this.touches.size > 1) { this.endStroke(); return; }
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const p = this.pick(e); if (!p) return;
      this.pending = this.inst?.snapshot?.();
      this.stroke = e.pointerId; this.inst?.pointer?.({ type: "down", ...p });
    };
    this.onMove = (e) => {
      const p = this.s.interact && (e.target === this.renderer.domElement || this.stroke === e.pointerId) ? this.pick(e) : null;
      this.hover = p;
      if (this.stroke === e.pointerId && p) this.inst?.pointer?.({ type: "move", ...p });
    };
    this.onUp = (e) => { this.touches.delete(e.pointerId); if (this.stroke === e.pointerId) this.endStroke(); };
    this.onLeave = () => (this.hover = null);
    el.addEventListener("pointerdown", this.onDown); el.addEventListener("pointerleave", this.onLeave);
    window.addEventListener("pointermove", this.onMove); window.addEventListener("pointerup", this.onUp); window.addEventListener("pointercancel", this.onUp);
  }
  endStroke() {
    if (this.stroke == null) return; this.stroke = null; this.inst?.pointer?.({ type: "up" });
    if (this.pending && this.inst?.changedSince?.(this.pending) !== false) { this.undoStack.push(this.pending); if (this.undoStack.length > 50) this.undoStack.shift(); this.redoStack = []; this.emitHistory(); }
    this.pending = null;
  }
  pick(e) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return null;
    this.ndc.set(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    const { origin: o, direction: d } = this.raycaster.ray;
    if (d.y > -1e-4) return null;
    let y = this.ctx.top * 0.75, x = 0, z = 0;
    for (let k = 0; k < 3; k++) { const t = (y - o.y) / d.y; x = o.x + d.x * t; z = o.z + d.z * t; y = this.sampleY(x, z); }
    const h = this.ctx.half;
    if (Math.abs(x) > h + 0.3 || Math.abs(z) > h + 0.3) return null;
    return { x, z, u: x / h, v: z / h, pressure: e.pointerType === "mouse" ? 1 : e.pressure > 0 ? e.pressure : 0.6, shift: e.shiftKey, alt: e.altKey };
  }

  // ---------- physics ----------
  physics(dt) {
    const { H, V } = this, T = this.ctx.target, n = H.length;
    const k = 120 * this.s.speed * this.s.speed, c = 2 * Math.sqrt(k) * 0.55;
    const steps = Math.max(1, Math.ceil((dt * Math.sqrt(k)) / 0.35)), h = dt / steps;
    let moving = 0, energy = 0;
    for (let s = 0; s < steps; s++) {
      const last = s === steps - 1;
      for (let i = 0; i < n; i++) {
        let v = V[i] + (k * (T[i] - H[i]) - c * V[i]) * h, y = H[i] + v * h;
        if (y > 1) { y = 1; v = 0; } else if (y < -1) { y = -1; v = 0; }
        H[i] = y; V[i] = v;
        if (last) { const a = v < 0 ? -v : v; if (a > 0.02) moving++; energy += v * v; }
      }
    }
    this.moving = moving; this.energyAcc = energy / n;
  }

  writeSurface() {
    const g = this.surf.geometry, pos = g.attributes.position.array, H = this.H, tr = this.ctx.travel * 0.5;
    for (let i = 0, n = H.length; i < n; i++) pos[i * 3 + 1] = CLEAR + (H[i] + 1) * tr;
    g.attributes.position.needsUpdate = true; if (this.style === "surface") g.computeVertexNormals();
    const col = g.attributes.color;
    if (this.s.colorize && this.inst?.writesColors) { col.array.set(this.ctx.colors); col.needsUpdate = true; this.colorsDirty = true; }
    else if (this.colorsDirty) { col.array.fill(1); col.needsUpdate = true; this.colorsDirty = false; }
  }
  snapshot() { return this.inst?.snapshot?.(); }
  restoreSnapshot(s) { if (s && this.inst?.restore) { try { this.inst.restore(s); } catch { /* incompatible grid */ } } }

  writeInstances() {
    if (!this.pins) return this.writeSurface();
    const arr = this.pins.instanceMatrix.array, H = this.H, tr = this.ctx.travel * 0.5;
    for (let i = 0, n = H.length; i < n; i++) arr[i * 16 + 13] = CLEAR + (H[i] + 1) * tr;
    this.pins.instanceMatrix.needsUpdate = true;
    const ic = this.pins.instanceColor;
    if (this.s.colorize && this.inst?.writesColors) { ic.array.set(this.ctx.colors); ic.needsUpdate = true; this.colorsDirty = true; }
    else if (this.colorsDirty) { ic.array.fill(1); ic.needsUpdate = true; this.colorsDirty = false; }
  }

  loop(now) {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.max(0, Math.min((now - this.last) / 1000, 0.05)); this.last = now; this.frames++;
    this.updateTween(dt); this.controls.update();
    if (!this.s.paused && this.inst) {
      const sdt = dt * this.s.simSpeed; this.simT += sdt;
      this.inst.step(sdt, this.simT); this.physics(dt);
    }
    this.writeInstances();
    const br = this.inst?.brushRadius?.();
    if (br && this.hover && this.s.interact) {
      this.ring.visible = true; this.ring.scale.setScalar(br);
      this.ring.position.set(this.hover.x, this.sampleY(this.hover.x, this.hover.z) + 0.03, this.hover.z);
    } else this.ring.visible = false;
    this.holo.update(now / 1000, this.ctx.top);
    this.draw();
    const q = this.q; q.n++; if (!q.start) q.start = now;
    if (now - q.start > 2500) { const fps = (q.n * 1000) / (now - q.start); q.start = now; q.n = 0; if (fps < 26 && q.level < 3 && !this.software) this.degrade(++q.level); }
    if (now - this.telT > 300) {
      const H = this.H; let sum = 0, mn = 1, mx = -1;
      for (let i = 0; i < H.length; i++) { const v = H[i]; sum += v; if (v < mn) mn = v; if (v > mx) mx = v; }
      this.onTelemetry?.({
        fps: Math.round((this.frames * 1000) / (now - this.telT)), pins: H.length, moving: this.moving || 0,
        energy: this.energyAcc * 1000, mean: sum / H.length, min: mn, max: mx, items: this.inst?.telemetry?.() || [], simT: this.simT,
      });
      this.frames = 0; this.telT = now;
    }
  }

  resize() {
    const w = this.container.clientWidth, h = this.container.clientHeight; if (!w || !h) return;
    this.renderer.setSize(w, h); this.camera.aspect = w / h;
    this.camera.fov = w < h ? 50 : 38; this.camera.updateProjectionMatrix();
    const f = w < h ? Math.min(2.2, Math.max(1, 0.7 / this.camera.aspect)) : 1;
    if (f !== this.camFactor) { const first = !this.camFactor; this.camFactor = f; if (first) this.camera.position.multiplyScalar(f); else this.setView(this.view); }
  }

  draw() { this.renderer.render(this.scene, this.camera); }
  degrade(level) {
    const r = this.renderer;
    if (level === 1) {
      r.shadowMap.enabled = false; this.key.castShadow = false;
      this.scene.traverse((o) => { if (o.material) o.material.needsUpdate = true; });
      r.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
    } else if (level === 2) { r.setPixelRatio(1); this.holo.motes.visible = false; this.holo.rings.forEach((x) => (x.visible = false)); }
    else if (level === 3 && this.s.density > 48) {
      const d = Math.max(40, Math.round((this.s.density * 0.7) / 8) * 8);
      this.setSettings({ density: d }); this.onAuto?.({ density: d });
    }
    this.resize(); this.onQuality?.(level);
  }
  exportPNG() {
    this.ring.visible = false; this.draw();
    download(this.renderer.domElement.toDataURL("image/png"), `matter-lab-${this.def?.id}-${Date.now()}.png`);
  }
  saveJSON() {
    const data = { app: "Digital Matter Lab", mode: this.def?.id, savedAt: new Date().toISOString(), settings: this.s, params: this.ctx.params, grid: this.ctx.N, heights: Array.from(this.H, (v) => +v.toFixed(3)) };
    const url = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: "application/json" }));
    download(url, `matter-lab-${this.def?.id}-${Date.now()}.json`); setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  dispose() {
    cancelAnimationFrame(this.raf); this.ro.disconnect(); this.inst?.dispose?.();
    window.removeEventListener("pointermove", this.onMove); window.removeEventListener("pointerup", this.onUp); window.removeEventListener("pointercancel", this.onUp);
    this.controls.dispose(); this.renderer.dispose(); this.renderer.domElement.remove();
  }
}
